import React from "react";

export class ScrollHelper extends React.PureComponent {
  performAlignment(target, marker) {
    this.target = target;
    this.marker = marker;
    if (target) {
      this.renderAlignment();
      if (marker) {
        this.showLine(true);
        window.addEventListener("resize", this.handleResize, false);
      }
    }
  }

  releaseAlignment() {
    window.removeEventListener("resize", this.handleResize, false);
    this.showLine(false);
    // once the items become un-aligned we want to "unhook"
    this.marker = null;
    this.target = null;
  }

  renderAlignment() {
    let { target, marker, scrollElement } = this;

    let targetBox = target.getBoundingClientRect();
    let targetAnchorY = (targetBox.top + targetBox.bottom) / 2;

    let markerBox, markerAnchorY;
    if (marker) {
      markerBox = marker.getBoundingClientRect();
      markerAnchorY = (markerBox.top + markerBox.bottom) / 2;
    } else {
      markerAnchorY =
        scrollElement.getBoundingClientRect().top + targetBox.height;
    }
    this.scrollContentBy(targetAnchorY - markerAnchorY);

    let { lineElement } = this;
    if (lineElement && marker) {
      let lineParent =
        lineElement.offsetParent || document.querySelector("#report-graph"); // HACK: offsetParent only known when displayed!
      let lineParentBox = lineParent.getBoundingClientRect();
      lineElement.style.top = markerAnchorY - lineParentBox.top + "px";
      lineElement.style.left = markerBox.right - lineParentBox.left + "px";
      lineElement.style.right = lineParentBox.right - targetBox.left + "px";
    }
  }

  checkAlignment() {
    let { marker, target } = this;

    let isAligned = false;
    if (marker && target) {
      let rowBox = target.getBoundingClientRect();
      let markerBox = marker.getBoundingClientRect();
      let markerAnchorY = (markerBox.top + markerBox.bottom) / 2;

      isAligned = rowBox.top < markerAnchorY && markerAnchorY < rowBox.bottom;
    }
    if (!isAligned) this.releaseAlignment();
  }

  scrollContentBy(delta) {
    let { scrollElement } = this;

    let contentElement = scrollElement.querySelector(".scroll-content-wrapper");
    let currentPaddingAbove = parseFloat(contentElement.style.paddingTop || 0);
    let currentPaddingBelow = parseFloat(
      contentElement.style.paddingBottom || 0,
    );
    let contentHeight =
      contentElement.scrollHeight - currentPaddingAbove - currentPaddingBelow;
    let maxScrollTop = contentHeight - scrollElement.clientHeight;
    let minScrollTop = 0; // …natch

    // NOTE: scroll top property is integral, and converting ourselves makes checks below more accurate
    let desiredScrollTop =
      delta === null
        ? 0
        : Math.round(scrollElement.scrollTop - currentPaddingAbove + delta);

    let paddingAbove = 0;
    if (desiredScrollTop < minScrollTop) {
      paddingAbove = minScrollTop - desiredScrollTop;
      desiredScrollTop = minScrollTop;
    }

    let paddingBelow = paddingAbove;
    if (desiredScrollTop > maxScrollTop) {
      paddingBelow = desiredScrollTop - maxScrollTop;
    }

    scrollElement.style.height = paddingAbove + contentHeight + "px";
    contentElement.style.paddingTop = paddingAbove + "px";
    contentElement.style.paddingBottom = paddingBelow + "px";
    scrollElement.scrollTop = desiredScrollTop;
  }

  cleanupPadding() {
    // "take up the slack" of any padding as the user scrolls
    // i.e. prevent the user from scrolling back
    this.scrollContentBy(0);
  }

  resetScrolling() {
    // scroll back to top
    this.scrollContentBy(null);

    // ~HACK: also reset the body scrolling to 0 when this happens
    // see https://groups.google.com/a/chromium.org/forum/#!msg/blink-dev/75hVThfrJ08/YD9kSgQljFEJ
    // and http://stackoverflow.com/questions/23313092/set-scrolltop-cross-browser
    // and https://miketaylr.com/posts/2014/11/document-body-scrolltop.html
    let scrollerCompat = document.scrollingElement || document.body;
    let scrollerStandard = document.documentElement || document.body.parentNode;
    scrollerCompat.scrollTop = scrollerStandard.scrollTop = 0;
  }

  componentDidMount() {
    let { scrollHelperState } = this.props;
    if (scrollHelperState) this.handleTrigger(scrollHelperState);
  }

  componentDidUpdate(prevProps) {
    let { activeObject, scrollHelperState } = this.props;
    if (scrollHelperState) {
      this.handleTrigger(scrollHelperState);
    } else if (activeObject !== prevProps.activeObject) {
      // when "page" changes clear scrolling completely
      this.releaseAlignment();
      this.resetScrolling();
    } else {
      // any other change just hides line
      this.releaseAlignment();
      this.cleanupPadding();
    }
  }

  componentWillUnmount() {
    this.releaseAlignment();
  }

  handleResize = (evt) => {
    this.releaseAlignment();
  };

  handleTrigger(scrollHelperState) {
    let { marker } = scrollHelperState;
    let { selectedObject } = this.props;
    let target = selectedObject
      ? this.getTargetRow(selectedObject._level, selectedObject.slug)
      : null;
    if (target) this.performAlignment(target, marker);
    else console.warn("ScrollHelper was triggered, but no target found!");
  }

  handleScroll = (evt) => {
    this.cleanupPadding();
    this.checkAlignment();
  };

  updateReference = (el) => {
    if (this.scrollElement) {
      this.scrollElement.removeEventListener(
        "scroll",
        this.handleScroll,
        false,
      );
    }
    if (el) {
      el.addEventListener("scroll", this.handleScroll, false);
    }
    this.scrollElement = el;

    // HACK: we also track the line element here…
    this.lineElement = document.querySelector("#report-graph .connecting-line");
  };

  getTargetRow(level, slug) {
    let objectPath = `${level}/${slug}`;
    return this.scrollElement.querySelector(`tr[data-path="${objectPath}"]`);
  }

  showLine(visible) {
    let { lineElement } = this;
    if (lineElement) {
      lineElement.style.display = visible ? "block" : "none";
    }
  }

  render() {
    let { children } = this.props;
    return (
      <div className="scroll-helper" ref={this.updateReference}>
        <div className="scroll-content-wrapper">{children}</div>
      </div>
    );
  }
}
